import React, {
  useEffect,
  useCallback,
  useContext,
  useState,
  useMemo,
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { Api } from '../../api/api';
import { actions } from '../../state';
import { config } from '../../api/config';
import {
  generateHeader,
  isEmpty,
  mapServiceTypes,
  mapServicesStatus,
  checkStatusColor,
  isFilterActive,
  buildTableFilterBody,
  errorMessage,
} from '../../utils';
import {
  SelectFilter,
  WithSpinner,
  Table,
  TableRow,
  TableCell,
  Checkbox,
} from '../../components';
import { ServicesContext } from './services.context';
import { LocationsContext } from './locations.context';
import BulkPricingDrawer from './bulkPricing/bulkPricingDrawer';
import BulkPricingConfirmationModal from './bulkPricing/bulkPricingConfirmationModal';
import {
  BULK_PRICING_SERVICE_TYPES,
  createEmptyBulkForm,
  createEmptyTouched,
} from './bulkPricing/bulkPricing.constants';
import {
  buildBulkUpdatePayload,
  buildPreviewRows,
  hasAnyChanges,
  isVacuumBulkPricingComplete,
  formatTableDuration,
  formatTablePrice,
} from './bulkPricing/bulkPricing.utils';

import styles from './servicesTable.module.scss';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const ServicesTable = ({
  dispatch,
  loading,
  services: { services, header, footer, total },
  timeRangeFilter,
  getFilteredBody,
  clearAllFilters,
  setClearToFalse,
}) => {
  const { bulkPricingMode, setBulkPricingMode } = useContext(LocationsContext);
  const {
    filterBody,
    setFilterBody,
    typeFilter,
    setTypeFilter,
    locationFilter,
    setLocationFilter,
    statusFilter,
    setStatusFilter,
    sortOrder,
    setSortOrder,
  } = useContext(ServicesContext);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [lockedServiceType, setLockedServiceType] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [touched, setTouched] = useState({});
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const selectedRows = useMemo(
    () => services.filter(row => selectedIds.includes(row.id)),
    [services, selectedIds]
  );

  const selectedLocationIds = useMemo(
    () => [...new Set(selectedRows.map(row => row.location_id))],
    [selectedRows]
  );

  const fetchServices = useCallback(
    data => {
      dispatch(actions.services.getServicesAction(data));
    },
    [dispatch]
  );

  const clearServices = useCallback(() => {
    dispatch(actions.services.clearServicesAction());
  }, [dispatch]);

  useEffect(() => () => clearServices(), [clearServices]);

  const clearSelectedLocation = useCallback(() => {
    dispatch(actions.locations.setSelectedLocationAction({}));
  }, [dispatch]);

  useEffect(() => {
    if (!bulkPricingMode) {
      setSelectedIds([]);
      setLockedServiceType(null);
      setConfirmOpen(false);
    }
  }, [bulkPricingMode]);

  useEffect(() => {
    let filterToSubmit;

    if (filterBody) {
      if (clearAllFilters) {
        setLocationFilter([]);
        setStatusFilter([]);
        setTypeFilter([]);
        setFilterBody();
        setSortOrder('asc');
        setClearToFalse(false);
        setPage(1);
      } else if (timeRangeFilter) {
        const filterSum = { ...filterBody, ...timeRangeFilter };
        filterToSubmit = filterSum;
      } else {
        filterToSubmit = filterBody;
      }
    } else if (timeRangeFilter) {
      filterToSubmit = timeRangeFilter;
    }
    const offset = (page - 1) * PAGE_SIZE;
    const submitBody = filterToSubmit
      ? { ...filterToSubmit, offset, limit: PAGE_SIZE }
      : { offset, limit: PAGE_SIZE };
    getFilteredBody(submitBody);
    fetchServices(submitBody);
  }, [
    fetchServices,
    filterBody,
    timeRangeFilter,
    getFilteredBody,
    clearAllFilters,
    setClearToFalse,
    page,
    setFilterBody,
    setLocationFilter,
    setStatusFilter,
    setTypeFilter,
    setSortOrder,
  ]);

  const handleSubmit = submitOptions => {
    const generatedFilter = [];

    if (locationFilter.length) {
      generatedFilter.push({ field: 'full_address', value: locationFilter });
    }
    if (statusFilter.length) {
      generatedFilter.push({ field: 'status', value: statusFilter });
    }
    if (typeFilter.length) {
      generatedFilter.push({ field: 'type', value: typeFilter });
    }

    setFilterBody(
      buildTableFilterBody({
        submitOptions,
        generatedFilter,
        sortOrder,
        extraBody: timeRangeFilter || {},
      })
    );
    setPage(1);
  };

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const resetBulkForm = serviceType => {
    setFormValues(createEmptyBulkForm(serviceType));
    setTouched(createEmptyTouched(serviceType));
  };

  useEffect(() => {
    if (lockedServiceType) {
      resetBulkForm(lockedServiceType);
    }
  }, [lockedServiceType]);

  const isRowDisabled = row => {
    if (!bulkPricingMode) {
      return false;
    }
    if (!BULK_PRICING_SERVICE_TYPES.includes(row.type)) {
      return true;
    }
    if (lockedServiceType === null) {
      return false;
    }
    return row.type !== lockedServiceType;
  };

  const handleRowSelect = (row, checked) => {
    if (isRowDisabled(row)) {
      return;
    }

    if (checked) {
      const nextType = lockedServiceType ?? row.type;
      setLockedServiceType(nextType);
      setSelectedIds(prev =>
        prev.includes(row.id) ? prev : [...prev, row.id]
      );
      return;
    }

    const nextIds = selectedIds.filter(id => id !== row.id);
    setSelectedIds(nextIds);
    if (!nextIds.length) {
      setLockedServiceType(null);
      setConfirmOpen(false);
    }
  };

  const handleRowClick = row => {
    if (!bulkPricingMode || isRowDisabled(row)) {
      return;
    }
    handleRowSelect(row, !selectedIds.includes(row.id));
  };

  const handleSelectAll = ({ target: { checked } }) => {
    if (!checked) {
      setSelectedIds([]);
      setLockedServiceType(null);
      return;
    }

    const selectable = services.filter(
      row =>
        BULK_PRICING_SERVICE_TYPES.includes(row.type) &&
        (lockedServiceType === null || row.type === lockedServiceType)
    );
    if (!selectable.length) {
      return;
    }

    const type = lockedServiceType ?? selectable[0].type;
    setLockedServiceType(type);
    setSelectedIds(
      selectable.filter(row => row.type === type).map(row => row.id)
    );
  };

  const handleFormChange = ({ target: { name, value } }) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleDrawerClose = () => {
    setSelectedIds([]);
    setLockedServiceType(null);
    setConfirmOpen(false);
  };

  const showDrawer =
    bulkPricingMode && selectedIds.length > 0 && lockedServiceType !== null;

  const handleUpdateClick = async () => {
    if (!hasAnyChanges(touched)) {
      dispatch(
        actions.error.setErrorAction({
          msg: 'Įveskite bent vieną parametrą atnaujinimui',
        })
      );
      return;
    }

    if (lockedServiceType === 24 && !isVacuumBulkPricingComplete(formValues)) {
      dispatch(
        actions.error.setErrorAction({
          msg: 'Siurbliui reikia nurodyti visas 3/6/9 min kainas',
        })
      );
      return;
    }

    setPreviewLoading(true);
    try {
      const locations = await Promise.all(
        selectedLocationIds.map(id => Api.getLocation(id))
      );
      const rows = buildPreviewRows({
        locations,
        serviceType: lockedServiceType,
        formValues,
        touched,
      });
      setPreviewRows(rows);
      setConfirmOpen(true);
    } catch (e) {
      dispatch(actions.error.setErrorAction(errorMessage(e)));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirm = async () => {
    const payload = buildBulkUpdatePayload({
      serviceType: lockedServiceType,
      locationIds: selectedLocationIds,
      formValues,
      touched,
    });

    if (!payload) {
      return;
    }

    setSubmitting(true);
    try {
      await Api.bulkPricingUpdate(payload);
      dispatch(
        actions.error.setAlertAction(
          'Kainodara sėkmingai atnaujinta',
          'success'
        )
      );
      setConfirmOpen(false);
      setSelectedIds([]);
      setLockedServiceType(null);
      setBulkPricingMode(false);
      const offset = (page - 1) * PAGE_SIZE;
      const submitBody = filterBody
        ? { ...filterBody, offset, limit: PAGE_SIZE }
        : { offset, limit: PAGE_SIZE };
      fetchServices(submitBody);
    } catch (e) {
      dispatch(actions.error.setErrorAction(errorMessage(e)));
    } finally {
      setSubmitting(false);
    }
  };

  const selectableOnPage = services.filter(
    row =>
      BULK_PRICING_SERVICE_TYPES.includes(row.type) &&
      (lockedServiceType === null || row.type === lockedServiceType)
  );
  const allSelectableSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every(row => selectedIds.includes(row.id));
  const tableHeader = [
    ...(bulkPricingMode
      ? [
          <div className={styles.selectionHeader} key="selection">
            <Checkbox
              label=""
              name="select-all"
              value="all"
              checked={allSelectableSelected}
              onChange={handleSelectAll}
              dense
              customId="services-bulk"
              className={styles.selectionCheckbox}
            />
          </div>,
        ]
      : []),
    <SelectFilter
      title="Tipas"
      name="type"
      valueType="number"
      array={generateHeader(header.type, mapServiceTypes)}
      filterState={typeFilter}
      setFilterState={setTypeFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', typeFilter)}
    />,
    <SelectFilter
      title="Lokacija"
      name="full_address"
      valueType="string"
      array={generateHeader(header.full_address)}
      filterState={locationFilter}
      setFilterState={setLocationFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', locationFilter)}
    />,
    <SelectFilter
      title="Būsena"
      name="status"
      valueType="number"
      array={generateHeader(header.status, mapServicesStatus)}
      filterState={statusFilter}
      setFilterState={setStatusFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', statusFilter)}
    />,
    'Trukmė',
    'Kaina su PVM, EUR',
  ];

  const tableFooter = bulkPricingMode
    ? ['', '', '', '', '', '']
    : ['', '', '', '', footer?.duration ?? '', footer?.price ?? ''];

  const selectionCellClass = bulkPricingMode ? styles.selectionCell : null;
  const headerCellClassNames = bulkPricingMode
    ? [styles.selectionCell, null, null, null, null, null]
    : [];
  const footerCellClassNames = bulkPricingMode
    ? [styles.selectionCell, null, null, null, null, null]
    : [];

  const renderTable = () =>
    services.map(row => {
      const {
        id,
        full_address,
        status,
        type,
        location_id,
        duration,
        price,
      } = row;
      const disabled = isRowDisabled(row);
      const checked = selectedIds.includes(id);

      return (
        <TableRow
          onClick={
            bulkPricingMode ? () => handleRowClick(row) : clearSelectedLocation
          }
          url={
            bulkPricingMode ? null : config.APP_PATHS.editLocation(location_id)
          }
          key={id}
          disabled={disabled}
          className={cn(
            bulkPricingMode && styles.bulkRow,
            bulkPricingMode && disabled && styles.bulkRowDisabled
          )}
        >
          {bulkPricingMode && (
            <TableCell
              className={selectionCellClass}
              onClick={event => event.stopPropagation()}
            >
              <Checkbox
                label=""
                name={`select-${id}`}
                value={id}
                checked={checked}
                disabled={disabled}
                customId="services-bulk"
                className={styles.selectionCheckbox}
                onChange={event => handleRowSelect(row, event.target.checked)}
                dense
                success={checked}
              />
            </TableCell>
          )}
          <TableCell>{mapServiceTypes(type)}</TableCell>
          <TableCell>{full_address}</TableCell>
          <TableCell>
            <span style={{ color: checkStatusColor(status) }}>
              {mapServicesStatus(status)}
            </span>
          </TableCell>
          <TableCell>{formatTableDuration(duration)}</TableCell>
          <TableCell align="right">{formatTablePrice(price)}</TableCell>
        </TableRow>
      );
    });

  return (
    <WithSpinner loading={loading}>
      {!isEmpty(header) && (
        <Table
          header={tableHeader}
          footer={tableFooter}
          filter={true}
          isEmpty={!services.length}
          width="xs"
          headerCellClassNames={headerCellClassNames}
          footerCellClassNames={footerCellClassNames}
          rightAlignedFooterCells={[bulkPricingMode ? 5 : 4]}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          serverPagination={true}
          onPageChange={setPage}
        >
          {renderTable()}
        </Table>
      )}

      {showDrawer && (
        <BulkPricingDrawer
          serviceType={lockedServiceType}
          values={formValues}
          onChange={handleFormChange}
          onClose={handleDrawerClose}
          onUpdate={handleUpdateClick}
          submitting={previewLoading}
        />
      )}

      {confirmOpen && (
        <BulkPricingConfirmationModal
          rows={previewRows}
          totalSelectedLocations={selectedLocationIds.length}
          loading={submitting}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </WithSpinner>
  );
};

const mapStateToProps = state => ({
  services: state.servicesTable,
  loading: state.locations.loading,
});

ServicesTable.propTypes = {
  dispatch: PropTypes.func.isRequired,
  services: PropTypes.shape({
    services: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    header: PropTypes.object.isRequired,
    footer: PropTypes.object.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  timeRangeFilter: PropTypes.object,
  getFilteredBody: PropTypes.func.isRequired,
  clearAllFilters: PropTypes.bool.isRequired,
  setClearToFalse: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(ServicesTable);
